import React, { useState, useEffect, useCallback } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../../api/axios';
import { SyncManager } from '../../utils/SyncManager';
import { FiCalendar, FiTrendingUp } from 'react-icons/fi';
import './SalesChart.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Helper for exact YYYY-MM-DD (No business logic, just formatting)
const formatYYYYMMDD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Business Date Helper 
 * Grouping logic: Orders before 4 PM belong to the previous day
 */
const getBusinessDateString = (dateInput) => {
    const d = new Date(dateInput);
    const hour = d.getHours();
    if (hour < 16) {
        d.setDate(d.getDate() - 1);
    }
    return formatYYYYMMDD(d);
};

const SalesChart = ({ branchQuery = '' }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initial state setup using business context
    const now = new Date();
    const bizToday = getBusinessDateString(now);

    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    // Note: If today is Feb 1st 2 AM, bizToday is Jan 31st. 
    // We stay in the current range for simplicity.

    const [startDate, setStartDate] = useState(getBusinessDateString(firstDayOfMonth));
    const [endDate, setEndDate] = useState(bizToday);

    // Summary stats
    const [summary, setSummary] = useState({ totalSales: 0, totalOrders: 0, avgDailySales: 0 });

    const fetchSalesData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const baseQuery = branchQuery ? `?${branchQuery}` : '';
            const url = `/reports/sales-range${baseQuery}${baseQuery ? '&' : '?'}startDate=${startDate}&endDate=${endDate}`;

            // 1. Fetch Synced Data (from API or Cache)
            let resData = [];
            try {
                const res = await api.get(url);
                resData = res.data || [];
            } catch (apiErr) {
                console.warn('Chart API failed, proceeding with offline base');
            }

            // Map server data to dateMap
            const dateMap = {};
            resData.forEach(d => {
                const dateStr = typeof d.date === 'string' ? d.date.slice(0, 10) : formatYYYYMMDD(d.date);
                dateMap[dateStr] = {
                    sales: parseFloat(d.total_sales || 0),
                    orders: parseInt(d.total_orders || 0)
                };
            });

            // 2. Fetch & Merge Pending Offline Orders
            const pendingOrders = await SyncManager.getPendingOrders();
            pendingOrders.forEach(order => {
                const bizDateStr = getBusinessDateString(order.createdAt);

                // Only merge if it's within the requested range
                if (bizDateStr >= startDate && bizDateStr <= endDate) {
                    if (!dateMap[bizDateStr]) {
                        dateMap[bizDateStr] = { sales: 0, orders: 0 };
                    }
                    dateMap[bizDateStr].sales += parseFloat(order.totalAmount || 0);
                    dateMap[bizDateStr].orders += 1;
                }
            });

            // 3. Generate Timeline (Fill Gaps)
            const allDates = [];
            let current = new Date(startDate + 'T04:00:00'); // Mid-day to avoid offset issues
            const end = new Date(endDate + 'T23:59:59');

            while (current <= end) {
                allDates.push(formatYYYYMMDD(current));
                current.setDate(current.getDate() + 1);
            }

            const labels = allDates.map(d => {
                const parts = d.split('-');
                const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            });

            const salesValues = allDates.map(d => dateMap[d]?.sales || 0);

            // 4. Calculate Final Stats
            const totalSales = salesValues.reduce((a, b) => a + b, 0);
            const totalOrders = allDates.reduce((sum, d) => sum + (dateMap[d]?.orders || 0), 0);
            const daysWithSales = salesValues.filter(s => s > 0).length;
            const avgDailySales = daysWithSales > 0 ? totalSales / daysWithSales : 0;

            setSummary({ totalSales, totalOrders, avgDailySales });

            setChartData({
                labels,
                datasets: [
                    {
                        label: 'Sales (Rs.)',
                        data: salesValues,
                        borderColor: '#FF6B35',
                        backgroundColor: (context) => {
                            const ctx = context.chart.ctx;
                            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                            gradient.addColorStop(0, 'rgba(255, 107, 53, 0.4)');
                            gradient.addColorStop(1, 'rgba(255, 107, 53, 0.05)');
                            return gradient;
                        },
                        fill: true,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointBackgroundColor: '#FF6B35',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        borderWidth: 3,
                    },
                ],
            });
        } catch (err) {
            console.error('SalesChart Fetch Error:', err);
            setError('Failed to refresh chart data');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, branchQuery]);

    useEffect(() => {
        fetchSalesData();
    }, [fetchSalesData]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(26, 32, 44, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: 'rgba(255, 107, 53, 0.3)',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: (items) => items[0]?.label || '',
                    label: (item) => `Rs. ${parseFloat(item.raw).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#718096',
                    font: { size: 11 },
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 10
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
                ticks: {
                    color: '#718096',
                    font: { size: 11 },
                    callback: (value) => value === 0 ? '0' : `Rs. ${value.toLocaleString()}`,
                    maxTicksLimit: 6
                },
            },
        },
    };

    const setPreset = (preset) => {
        const today = new Date();
        let start;
        switch (preset) {
            case 'week':
                start = new Date(today);
                start.setDate(today.getDate() - 6);
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'last-month':
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                today.setDate(0);
                break;
            case 'quarter':
                start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                break;
            default:
                return;
        }
        setStartDate(getBusinessDateString(start));
        setEndDate(getBusinessDateString(preset === 'last-month' ? today : new Date()));
    };

    return (
        <div className="sales-chart-section">
            <div className="card sales-chart-card">
                <div className="card-header sales-chart-header">
                    <div className="chart-title">
                        <FiTrendingUp className="chart-title-icon" />
                        <h3>Daily Sales</h3>
                    </div>
                    <div className="chart-controls">
                        <div className="chart-presets">
                            <button
                                className={`preset-btn ${startDate === getBusinessDateString(new Date(new Date().setDate(new Date().getDate() - 6))) ? 'active' : ''}`}
                                onClick={() => setPreset('week')}
                            >
                                7 Days
                            </button>
                            <button
                                className={`preset-btn ${startDate === getBusinessDateString(new Date(new Date().getFullYear(), new Date().getMonth(), 1)) ? 'active' : ''}`}
                                onClick={() => setPreset('month')}
                            >
                                This Month
                            </button>
                            <button className="preset-btn" onClick={() => setPreset('last-month')}>
                                Last Month
                            </button>
                            <button className="preset-btn" onClick={() => setPreset('quarter')}>
                                3 Months
                            </button>
                        </div>
                        <div className="date-range-picker">
                            <FiCalendar className="date-icon" />
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="date-input"
                            />
                            <span className="date-separator">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="chart-summary-row">
                    <div className="chart-summary-item">
                        <span className="summary-label">Total Sales</span>
                        <span className="summary-value sales-color">
                            Rs. {summary.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                    <div className="chart-summary-item">
                        <span className="summary-label">Total Orders</span>
                        <span className="summary-value orders-color">{summary.totalOrders}</span>
                    </div>
                    <div className="chart-summary-item">
                        <span className="summary-label">Avg. Daily Sales</span>
                        <span className="summary-value avg-color">
                            Rs. {summary.avgDailySales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>

                <div className="card-body chart-body">
                    {loading ? (
                        <div className="chart-loading">
                            <div className="spinner"></div>
                            <p>Loading chart...</p>
                        </div>
                    ) : error ? (
                        <div className="chart-error">
                            <p>{error}</p>
                            <button className="btn btn-sm btn-primary" onClick={fetchSalesData}>Retry</button>
                        </div>
                    ) : chartData ? (
                        <div className="chart-container">
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default SalesChart;
